import React from "react";
import Immutable from "immutable";
import LabelledSelect from "js/common/views/inputs/labelled-select";
import Select from "js/common/views/inputs/immutable-react-select";
import NumberField from "js/common/views/inputs/number-field";
import TooltipLabel from "js/common/views/tooltip-label";

// ── Constants ─────────────────────────────────────────────────────────────────

// ── Schema helpers ────────────────────────────────────────────────────────────

const isPlaceholder = v =>
    v !== null && typeof v === "object" && !Array.isArray(v) && "placeholderType" in v;

const hasAnyPlaceholder = schema => {
  if (isPlaceholder(schema)) return true;
  if (Array.isArray(schema)) return schema.some(hasAnyPlaceholder);
  if (typeof schema === "object" && schema !== null) return Object.values(schema).some(hasAnyPlaceholder);
  return false;
};

// Type-level implicit defaults for placeholder types that always have a meaningful zero value.
// Matches what PlaceholderWidget renders when value is null and no explicit default is set.
const IMPLICIT_PLACEHOLDER_DEFAULTS = {
  "positive-integer": "0",
  "integer": "0",
};

// Named option lists for dropdown placeholders. Templates reference these via optionList.
// Eventually these could be loaded from a DB table; for now they are hardcoded here.
const OPTION_LISTS = {
  "numeric-comparator": [
    {value: "EQUALS", label: "equals"},
    {value: "NOT_EQUALS", label: "not equals"},
    {value: "LESS_THAN", label: "less than"},
    {value: "LESS_THAN_OR_EQUAL", label: "less than or equal"},
    {value: "GREATER_THAN", label: "greater than"},
    {value: "GREATER_THAN_OR_EQUAL", label: "greater than or equal"},
  ],
  "child-filter-type": [
    {value: "ALL", label: "All"},
    {value: "ANY", label: "Any"},
  ],
  "date-unit": [
    {value: "DAY", label: "Day(s)"},
    {value: "MONTH", label: "Month(s)"},
    {value: "YEAR", label: "Year(s)"},
  ],
  "timeframe": [
    {value: "TODAY", label: "Today"},
    {value: "LAST_7_DAYS", label: "Last 7 days"},
    {value: "LAST_30_DAYS", label: "Last 30 days"},
    {value: "LAST_90_DAYS", label: "Last 90 days"},
    {value: "LAST_12_MONTHS", label: "Last 12 months"},
    {value: "MTD", label: "This month"},
    {value: "YTD", label: "This year"},
  ],
};

// Merges schema structure into data so the output always contains:
//   • literals   — schema value always wins (non-editable, structural)
//   • nested obj — recursed
//   • placeholder — filled with schema/implicit default when data value is null
// Used only for rendering — onChange handlers spread `data` (params-only), not this result.
const completeDataWithSchema = (schema, data) => {
  if (typeof schema !== "object" || schema === null || Array.isArray(schema)) return data;
  const result = {...data};
  Object.entries(schema).forEach(([k, v]) => {
    if (k === "children") return;
    if (isPlaceholder(v)) {
      if (result[k] == null) {
        const def = v.default ?? IMPLICIT_PLACEHOLDER_DEFAULTS[v.placeholderType] ?? null;
        if (def != null) result[k] = def;
      }
    } else if (Array.isArray(v)) {
      const existingArr = Array.isArray(result[k]) ? result[k] : [];
      result[k] = v.map((item, i) =>
          typeof item === "object" && item !== null && !Array.isArray(item)
              ? completeDataWithSchema(item, existingArr[i] != null ? existingArr[i] : {})
              : item);
    } else if (typeof v === "object" && v !== null && !Array.isArray(v)) {
      result[k] = completeDataWithSchema(v, result[k] != null ? result[k] : {});
    } else {
      result[k] = v; // literal — schema is always the source of truth
    }
  });
  return result;
};

// Extracts only placeholder defaults from a schema — no literals, no structural values.
// Used to initialise aiParameters when a template is selected.
const paramsFromSchema = schema => {
  if (isPlaceholder(schema)) {
    const def = schema.default ?? IMPLICIT_PLACEHOLDER_DEFAULTS[schema.placeholderType] ?? null;
    return schema.optional && def === null ? undefined : def;
  }
  if (Array.isArray(schema)) {
    const items = schema.map(item => paramsFromSchema(item));
    const hasAny = items.some(r => r !== undefined);
    return hasAny ? items.map(r => r !== undefined ? r : {}) : undefined;
  }
  if (typeof schema === "object" && schema !== null) {
    const result = {};
    Object.entries(schema).forEach(([k, v]) => {
      if (k === "children") {
        const childSchemas = Array.isArray(v) ? v : [];
        if (childSchemas.length > 0) result.children = childSchemas.map(paramsFromSchema);
        return;
      }
      const val = paramsFromSchema(v);
      if (val !== undefined) result[k] = val;
    });
    return Object.keys(result).length > 0 ? result : undefined;
  }
  return undefined; // literal — excluded from params
};

// Reconstructs the full forwardReport by merging template schema literals on top of params.
// Literals always win; placeholder values come from params (with implicit defaults as fallback).
// Fields in params that are not in the schema (e.g. childFilterType when not defined there)
// are passed through as-is.
const buildForwardReport = (schema, params) => {
  if (typeof schema !== "object" || schema === null || Array.isArray(schema)) return params;

  const result = {};

  Object.entries(schema).forEach(([k, v]) => {
    if (k === "children") return;
    if (isPlaceholder(v)) {
      const val = params?.[k] ?? v.default ?? IMPLICIT_PLACEHOLDER_DEFAULTS[v.placeholderType] ?? null;
      if (val != null) result[k] = val;
    } else if (Array.isArray(v)) {
      result[k] = v.map((item, i) =>
          typeof item === "object" && item !== null && !Array.isArray(item)
              ? buildForwardReport(item, params?.[k]?.[i] ?? {})
              : item);
    } else if (typeof v === "object" && v !== null && !Array.isArray(v)) {
      const nested = buildForwardReport(v, params?.[k] ?? {});
      if (Object.keys(nested).length > 0) result[k] = nested;
    } else {
      result[k] = v; // literal from schema
    }
  });

  // Pass through params fields not covered by the schema (e.g. childFilterType).
  Object.entries(params || {}).forEach(([k, v]) => {
    if (k === "children" || k in schema) return;
    result[k] = v;
  });

  // Handle children.
  const templateChildren = Array.isArray(schema.children) ? schema.children : [];
  const paramChildren = Array.isArray(params?.children) ? params.children : [];
  if (templateChildren.length > 0) {
    const isDynamic = templateChildren.length === 1;
    if (isDynamic) {
      result.children = paramChildren.map(child => buildForwardReport(templateChildren[0], child));
    } else {
      result.children = templateChildren.map((cs, i) => buildForwardReport(cs, paramChildren[i] ?? {}));
    }
  }

  return result;
};

// ── Placeholder widget ────────────────────────────────────────────────────────
// Renders the appropriate input for a given placeholderType.

const PlaceholderWidget = ({placeholder, value, onChange, allKpis, entityTypeToLabel, entityLinks}) => {
  // For root "kpi" placeholders, filter by allowed entity list.
  // For child "kpi" placeholders, filter to KPIs that share the selected filterEntityType link.
  const filteredKpis = (allKpis || Immutable.List()).filter(k => {
    const entity = k.get("entity");
    const passesAllowed = !placeholder.allowed || placeholder.allowed.includes(entity);
    const passesFilterBy = !placeholder.filterBy || ((entityLinks || {})[entity] || []).includes(placeholder.filterBy);
    return passesAllowed && passesFilterBy;
  });

  const kpiOptions = filteredKpis
      .map(k => Immutable.Map({value: k.get("id"), label: k.get("name") || String(k.get("id"))}));

  switch (placeholder.placeholderType) {
    case "kpi":
      return (
          <div style={{minWidth: 220, flexShrink: 0}}>
            <Select
                isMulti={false}
                isClearable={false}
                placeholder={placeholder.placeholder || "Select metric"}
                options={kpiOptions}
                keys={["value", "label"]}
                selectedValue={value ?? null}
                onChange={v => onChange(v ?? null)} />
          </div>
      );

    case "grouping-entity": {
      const entityOptions = Immutable.List(placeholder.allowed || [])
          .map(e => Immutable.Map({value: e, label: entityTypeToLabel?.get(e) || e}))
          .sortBy(o => o.get("label"));
      return (
          <div style={{minWidth: 160, flexShrink: 0}}>
            <Select
                isMulti={false}
                isClearable={false}
                placeholder={placeholder.placeholder || "Select entity type"}
                options={entityOptions}
                keys={["value", "label"]}
                selectedValue={value || null}
                onChange={v => onChange(v || null)} />
          </div>
      );
    }

    case "positive-integer":
      return (
          <NumberField
              value={value != null ? parseInt(value) : (placeholder.default ?? 0)}
              range={[0]}
              onChange={v => onChange(String(v))}
              style={{width: 80}} />
      );

    case "integer":
      return (
          <NumberField
              value={value != null ? parseInt(value) : (placeholder.default ?? 0)}
              onChange={v => onChange(v)}
              style={{width: 80}} />
      );

    case "date":
      return (
          <input
              type="date"
              value={value || ""}
              onChange={e => onChange(e.target.value || null)}
              style={{fontSize: 13, padding: "2px 4px"}} />
      );

    case "dropdown": {
      const rawOptions = placeholder.optionList
          ? (OPTION_LISTS[placeholder.optionList] || [])
          : (placeholder.options || []).map(o => ({value: o, label: o}));
      const dropdownOptions = Immutable.fromJS(rawOptions);
      const defaultVal = placeholder.default ?? (rawOptions[0]?.value ?? null);
      return (
          <div style={{minWidth: 140, flexShrink: 0}}>
            <Select
                isMulti={false}
                isClearable={false}
                placeholder={placeholder.placeholder || "Select"}
                options={dropdownOptions}
                keys={["value", "label"]}
                selectedValue={value ?? defaultVal ?? null}
                onChange={v => onChange(v ?? null)} />
          </div>
      );
    }

    default:
      return <span style={{color: "red", fontSize: 12}}>[unknown: {placeholder.placeholderType}]</span>;
  }
};

// ── Template node ─────────────────────────────────────────────────────────────
// Recursively renders one node of the template schema. Only "kpiId",
// "filterEntityType", and "children" are special-cased; everything else is a
// black box where placeholders get an input by type and objects recurse.
//
// `data` contains only user-set param values (no schema literals). `d` merges
// in literals and defaults for rendering only — all onChange handlers spread
// `data` so only params bubble up.

const TemplateNode = ({schema, data, onChange, allKpis, entityTypeToLabel, entityLinks, filterEntityAllowed, depth = 0}) => {
  const d = completeDataWithSchema(schema, data || {});
  const isChildNode = filterEntityAllowed != null;

  // Derive entity/timeframe context from the kpiId selected at this level.
  const selectedKpiId = d.kpiId ?? null;
  const selectedKpi = selectedKpiId != null
      ? (allKpis || Immutable.List()).find(k => k.get("id") === selectedKpiId)
      : null;
  const selectedKpiEntity = selectedKpi?.get("entity") ?? null;
  const timeframeAllowed = selectedKpi ? selectedKpi.get("isOverridableDateColumn") !== false : true;
  const childFilterEntityAllowed = (entityLinks || {})[selectedKpiEntity] || [];

  const templateChildren = Array.isArray(schema.children) ? schema.children : [];
  const dataChildren = Array.isArray(data?.children) ? data.children : [];

  // Single child in schema.children means it's a repeatable item template — dynamic.
  // Two or more children means they're positional (different schema per slot) — fixed.
  const isDynamic = templateChildren.length === 1;
  const dynamicItemSchema = isDynamic ? templateChildren[0] : null;

  const renderEntry = (key, schemaVal) => {
    if (key === "children" || key === "return" || key === "childFilterType") return null;
    if (!timeframeAllowed && (key === "timeframe" || key === "timeframeAdjustment")) return null;

    if (isPlaceholder(schemaVal)) {
      const label = schemaVal.label || key;

      // filterEntityType — apply parent entity restriction, reset kpiId on change
      if (schemaVal.placeholderType === "grouping-entity") {
        return (
            <div key={key} style={{marginBottom: 8}}>
              <div style={{fontSize: 12, color: "#888", marginBottom: 2}}>{label}</div>
              <PlaceholderWidget
                  placeholder={{...schemaVal, allowed: filterEntityAllowed || []}}
                  value={d[key] || null}
                  onChange={v => onChange({...data, [key]: v || null, kpiId: null})}
                  allKpis={allKpis}
                  entityTypeToLabel={entityTypeToLabel}
                  entityLinks={entityLinks} />
            </div>
        );
      }

      // kpiId — apply filterBy restriction; clear timeframe data when switching to INSTANT KPI
      if (schemaVal.placeholderType === "kpi") {
        const placeholder = isChildNode
            ? {...schemaVal, filterBy: d.filterEntityType || null}
            : schemaVal;
        const isReturnKpi = schema.return === true;
        return (
            <div key={key} style={{marginBottom: 8}}>
              <div style={{fontSize: 12, color: "#888", marginBottom: 2}}>
                {isReturnKpi
                    ? <TooltipLabel
                        label={label}
                        tooltip="This is the metric that will be returned, changing this metric will change the columns you will see" />
                    : label}
              </div>
              <PlaceholderWidget
                  placeholder={placeholder}
                  value={d[key] ?? null}
                  onChange={v => {
                    const newKpi = v != null ? (allKpis || Immutable.List()).find(k => k.get("id") === v) : null;
                    const newAllowed = newKpi ? newKpi.get("isOverridableDateColumn") !== false : true;
                    if (!newAllowed) {
                      const {timeframe, timeframeAdjustment, ...rest} = data || {};
                      onChange({...rest, [key]: v ?? null});
                    } else {
                      onChange({...data, [key]: v ?? null});
                    }
                  }}
                  allKpis={allKpis}
                  entityTypeToLabel={entityTypeToLabel}
                  entityLinks={entityLinks} />
            </div>
        );
      }

      // All other placeholders — generic input with label
      return (
          <div key={key} style={{marginBottom: 8}}>
            <div style={{fontSize: 12, color: "#888", marginBottom: 2}}>{label}</div>
            <PlaceholderWidget
                placeholder={schemaVal}
                value={d[key] ?? schemaVal.default ?? null}
                onChange={v => onChange({...data, [key]: v})}
                allKpis={allKpis}
                entityLinks={entityLinks} />
          </div>
      );
    }

    // Array — recurse into elements that contain placeholders; skip fully-literal arrays
    if (Array.isArray(schemaVal)) {
      if (!hasAnyPlaceholder(schemaVal)) return null;
      return (
          <div key={key}>
            {schemaVal.map((schemaItem, i) => (
                <TemplateNode
                    key={i}
                    schema={schemaItem}
                    data={d?.[key]?.[i] || {}}
                    onChange={nested => {
                      const currentArr = Array.isArray(data?.[key]) ? data[key] : [];
                      const newArr = schemaVal.map((_, j) => j === i ? nested : (currentArr[j] || {}));
                      onChange({...data, [key]: newArr});
                    }}
                    allKpis={allKpis}
                    entityTypeToLabel={entityTypeToLabel}
                    entityLinks={entityLinks}
                    filterEntityAllowed={null} />
            ))}
          </div>
      );
    }

    // Non-placeholder object — recurse without entity restrictions
    if (typeof schemaVal === "object" && schemaVal !== null && !Array.isArray(schemaVal)) {
      return (
          <TemplateNode
              key={key}
              schema={schemaVal}
              data={data?.[key] || {}}
              onChange={nested => onChange({...data, [key]: nested})}
              allKpis={allKpis}
              entityTypeToLabel={entityTypeToLabel}
              entityLinks={entityLinks}
              filterEntityAllowed={null} />
      );
    }

    return null;
  };

  const FIELD_ORDER = {filterEntityType: 0, kpiId: 1};
  const orderedEntries = Object.entries(schema)
      .sort(([a], [b]) => (FIELD_ORDER[a] ?? 2) - (FIELD_ORDER[b] ?? 2));

  return (
      <div>
        {orderedEntries.map(([key, val]) => renderEntry(key, val))}

        {templateChildren.length > 0 && (
            <div style={{marginTop: 8}}>
              {(isDynamic ? dataChildren : templateChildren).length > 1 && (() => {
                const schemaPlaceholder = isPlaceholder(schema.childFilterType) ? schema.childFilterType : {};
                const label = schemaPlaceholder.label ?? "Conditions match";
                const schemaDefault = schemaPlaceholder.default ?? "ALL";
                return (
                    <div style={{marginBottom: 8}}>
                      <div style={{fontSize: 12, color: "#888", marginBottom: 2}}>{label}</div>
                      <PlaceholderWidget
                          placeholder={{placeholderType: "dropdown", optionList: "child-filter-type", ...schemaPlaceholder}}
                          value={data?.childFilterType ?? schemaDefault}
                          onChange={v => onChange({...data, childFilterType: v ?? schemaDefault})}
                          allKpis={allKpis}
                          entityTypeToLabel={entityTypeToLabel}
                          entityLinks={entityLinks} />
                    </div>
                );
              })()}

              {(isDynamic ? dataChildren : templateChildren).map((childSchemaOrData, i) => {
                const childSchema = isDynamic ? dynamicItemSchema : childSchemaOrData;
                const childData = isDynamic ? childSchemaOrData : (dataChildren[i] || {});
                const childDepth = depth + 1;
                const cardBg = childDepth % 2 === 1 ? "#f5f5f5" : "#ffffff";
                return (
                    <div key={i} style={{
                      border: "1px solid #e0e0e0",
                      borderRadius: 6,
                      padding: "12px 14px",
                      backgroundColor: cardBg,
                      marginBottom: 8,
                      position: "relative",
                    }}>
                      {isDynamic && i > 0 && (
                          <button
                              onClick={() => onChange({...data, children: dataChildren.filter((_, idx) => idx !== i)})}
                              style={{
                                position: "absolute", top: 6, right: 8,
                                fontSize: 12, color: "#bbb", border: "none", background: "none",
                                cursor: "pointer", lineHeight: 1, padding: "2px 4px",
                              }}
                              title="Remove condition">
                            ✕
                          </button>
                      )}
                      <TemplateNode
                          schema={childSchema}
                          data={childData}
                          onChange={newChild => {
                            if (isDynamic) {
                              onChange({...data, children: dataChildren.map((c, idx) => idx === i ? newChild : c)});
                            } else {
                              onChange({...data, children: templateChildren.map((cs, idx) =>
                                  idx === i ? newChild : (dataChildren[idx] || paramsFromSchema(cs) || {}))});
                            }
                          }}
                          allKpis={allKpis}
                          entityTypeToLabel={entityTypeToLabel}
                          entityLinks={entityLinks}
                          filterEntityAllowed={childFilterEntityAllowed}
                          depth={childDepth} />
                    </div>
                );
              })}

              {isDynamic && (
                  <button
                      onClick={() => onChange({...data, children: [...dataChildren, paramsFromSchema(dynamicItemSchema) || {}]})}
                      style={{
                        display: "block", width: "100%", fontSize: 12, color: "#666",
                        border: "1px dashed #ccc", background: "none", borderRadius: 4,
                        padding: "6px 0", cursor: "pointer", textAlign: "center",
                      }}>
                    + Add condition
                  </button>
              )}
            </div>
        )}
      </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

const AiBuilder = ({kpi, onKpiChange, allKpis, entityTypeToLabel, aiBuilderTemplates, entityLinks}) => {
  const templates = aiBuilderTemplates || [];
  const templateOptions = Immutable.fromJS(templates.map(t => ({value: t.id, label: t.name})));

  const aiParameters = kpi.getIn(["config", "aiParameters"]) || Immutable.Map();
  const selectedTemplateId = kpi.getIn(["config", "aiTemplateId"]) ?? null;
  const selectedTemplate = selectedTemplateId != null ? (templates.find(t => t.id === selectedTemplateId) ?? null) : null;

  const handleTemplateChange = id => {
    const template = id != null ? (templates.find(t => t.id === id) ?? null) : null;
    if (!template) {
      onKpiChange(kpi.deleteIn(["config", "aiTemplateId"]));
      return;
    }
    // Preserve the current root metric if it's valid for the new template.
    const currentKpiId = aiParameters.get("kpiId") ?? null;
    const rootAllowed = template.schema.kpiId?.allowed || null;
    let preservedKpiId = null;
    if (currentKpiId != null && rootAllowed) {
      const entity = (allKpis || Immutable.List())
          .find(k => k.get("id") === currentKpiId)?.get("entity");
      if (entity && rootAllowed.includes(entity)) preservedKpiId = currentKpiId;
    }
    const initialParams = {
      ...paramsFromSchema(template.schema),
      ...(preservedKpiId != null ? {kpiId: preservedKpiId} : {}),
    };
    onKpiChange(kpi
        .setIn(["config", "aiTemplateId"], id)
        .setIn(["config", "aiParameters"], Immutable.fromJS(initialParams))
        .setIn(["config", "forwardReport"], Immutable.fromJS(buildForwardReport(template.schema, initialParams))));
  };

  const handleAiParametersChange = updated => {
    let params = updated.toJS ? updated.toJS() : updated;

    // When >1 children are present and childFilterType hasn't been set yet, seed it
    // so aiParameters always contains an explicit value while the selector is visible.
    if (Array.isArray(params.children) && params.children.length > 1 && params.childFilterType == null) {
      const schemaPlaceholder = isPlaceholder(selectedTemplate?.schema?.childFilterType)
          ? selectedTemplate.schema.childFilterType : {};
      params = {...params, childFilterType: schemaPlaceholder.default ?? "ALL"};
      updated = Immutable.fromJS(params);
    }

    const newKpiId = params.kpiId ?? null;
    const newKpi = newKpiId != null
        ? (allKpis || Immutable.List()).find(k => k.get("id") === newKpiId)
        : null;
    const isInstant = newKpi ? newKpi.get("isOverridableDateColumn") === false : null;

    let updatedKpi = kpi
        .setIn(["config", "aiParameters"], updated)
        .setIn(["config", "forwardReport"], Immutable.fromJS(buildForwardReport(selectedTemplate.schema, params)));
    if (isInstant === true) {
      updatedKpi = updatedKpi.set("overrideTrendable", false);
    } else if (isInstant === false) {
      updatedKpi = updatedKpi.set("overrideTrendable", null);
    }
    onKpiChange(updatedKpi);
  };

  return (
      <div style={{padding: "1rem"}}>
        <div style={{marginBottom: 16, display: "flex", alignItems: "flex-end", gap: 16}}>
          <LabelledSelect
              label="Template"
              isMulti={false}
              isClearable={false}
              placeholder="Select a template"
              options={templateOptions}
              keys={["value", "label"]}
              selectedValue={selectedTemplateId}
              onChange={handleTemplateChange}
              containerStyle={{width: 400, flexShrink: 0}} />
          {selectedTemplate?.description && (
              <p style={{margin: 0, fontSize: 13, color: "#888", paddingBottom: 8}}>
                {selectedTemplate.description}
              </p>
          )}
        </div>

        {selectedTemplate && (
            <TemplateNode
                schema={selectedTemplate.schema}
                data={aiParameters.toJS ? aiParameters.toJS() : aiParameters}
                onChange={newData => handleAiParametersChange(Immutable.fromJS(newData))}
                allKpis={allKpis}
                entityTypeToLabel={entityTypeToLabel}
                entityLinks={entityLinks}
                filterEntityAllowed={null} />
        )}

        {!selectedTemplate && (
            <p style={{color: "#aaa", fontSize: 13}}>Select a template above to start building.</p>
        )}
      </div>
  );
};

export default AiBuilder;
